import {
    Box,
    TextField,
    Typography,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Stack,
    Autocomplete,
} from '@mui/material';
import * as React from 'react';
import {
    MobileWealthDTO,
    MobileWealthVovClient,
} from '../../../ApiOld/Api/ApiServerVov';
import { useMobileApi } from '../../../Api/Mobile/useMobileApi';
import { useEffect, useState } from 'react';
import LoadingIndicator from './LoadingIndicator';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { useEmployeeContext } from '../../../Contexts/Providers/EmployeeLayoutProvider';
import PersonIcon from '@mui/icons-material/Person';
import { Hardware } from '@mui/icons-material';
import CheckIcon from '@mui/icons-material/Check';
import DomainOutlinedIcon from '@mui/icons-material/DomainOutlined';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { prioritizedSearch } from './SearchUtil';

export interface IWealthToBorrowListProps {
    onWealthClick: (wealthId: MobileWealthDTO | undefined) => void;
    selectedWealths: MobileWealthDTO[];
}

export default function WealthToBorrowList(props: IWealthToBorrowListProps) {
    const { onWealthClick, selectedWealths } = props;
    const { authorizedMobileEmployeeApi } = useMobileApi(MobileWealthVovClient);
    const { t, i18n } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { diagnosticAlerts } = useEmployeeContext();
    const [wealths, setWealths] = useState<MobileWealthDTO[] | undefined>();
    const [filteredWealths, setFilteredWealths] = useState<MobileWealthDTO[] | undefined>();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedEmployee, setSelectedEmployee] = useState('');
    const [loaded, setLoaded] = useState(false);
    const [filterType, setFilterType] = useState<'all' | 'available' | 'borrowed'>('all');

    useEffect(() => {
        authorizedMobileEmployeeApi
            .getWealthToBorrow()
            .then((response) => {
                if (response) {
                    setWealths(response.wealthToBorrow);
                    setFilteredWealths(response.wealthToBorrow);
                    setLoaded(true);
                    if (diagnosticAlerts)
                        enqueueSnackbar(t('WealthsLoadedSuccessfully'), { variant: 'success' });
                } else {
                    setLoaded(true);
                    enqueueSnackbar(t('NoWealthsWereFound'), { variant: 'error' });
                }
            })
            .catch(() => {
                setLoaded(true);
                enqueueSnackbar(t('ThereWasAnErrorLoadingWealths'), { variant: 'error' });
            });
    }, [authorizedMobileEmployeeApi, diagnosticAlerts, enqueueSnackbar, t]);

    useEffect(() => {
        if (wealths) {
            let filtered = searchTerm
                ? prioritizedSearch(
                    wealths,
                    searchTerm,
                    (wealth) => [wealth?.name || "", wealth?.code || ""]
                )
                : [...wealths];

            if (filterType === 'available') {
                filtered = filtered.filter((wealth) => !wealth.employeeCodeAndName);
            } else if (filterType === 'borrowed') {
                filtered = filtered.filter((wealth) => wealth.employeeCodeAndName && (!selectedEmployee || wealth.employeeCodeAndName === selectedEmployee));
            }

            filtered = filtered.filter((wealth) => !selectedWealths.some(selected => selected.wealthId === wealth.wealthId));

            setFilteredWealths(filtered);
        }
    }, [searchTerm, wealths, selectedEmployee, filterType, selectedWealths]);

    const uniqueEmployees = wealths
        ? Array.from(new Set(wealths.filter(x => x.employeeCodeAndName).map((wealth) => wealth.employeeCodeAndName)))
        : [];

    if (!loaded) {
        return <LoadingIndicator />;
    }

    return (
        <Box>
            <Select
                fullWidth
                size="small"
                id="employee-select"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as 'all' | 'available' | 'borrowed')}
                sx={{
                    '& .MuiInputLabel-outlined': { top: '-6px' },
                    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
                        backgroundColor: 'transparent',
                    },
                    mb: 1.5, background: 'white', boxShadow: 2, borderRadius: 4
                }}
            >
                <MenuItem value="all">{t('AllItems')}</MenuItem>
                <MenuItem value="available">{t('AvailableItems')}</MenuItem>
                <MenuItem disabled={uniqueEmployees.length === 0} value="borrowed">{t('BorrowedItems')}</MenuItem>
            </Select>
            {filterType === 'borrowed' && (
                <Autocomplete
                    disablePortal
                    fullWidth
                    options={uniqueEmployees.sort()}
                    getOptionLabel={(option) => option ?? ''}
                    value={selectedEmployee || null}
                    onChange={(event, newValue) => {
                        setSelectedEmployee(newValue || '');
                    }}
                    renderOption={(props, option) => (
                        <li {...props} style={{ color: wealths?.find(wealth => wealth.employeeCodeAndName === option)?.borrowedByCurrentEmployee ? 'green' : 'inherit' }}>
                            {option}
                        </li>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t('BorrowedBy')}
                            variant="outlined"
                            size="small"
                            sx={{
                                boxShadow: 2,
                                borderRadius: 4,
                                mb: 1,
                                background: 'white',
                                '& .MuiInputBase-input': {
                                    color: wealths?.find(wealth => wealth.employeeCodeAndName === selectedEmployee)?.borrowedByCurrentEmployee ? 'green' : 'inherit'
                                }
                            }}
                        />
                    )}
                />
            )}
            <TextField
                fullWidth
                size="small"
                sx={{ boxShadow: 2, borderRadius: 4, mb: 1, background: 'white' }}
                placeholder={t('Search...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                    endAdornment: <SearchOutlinedIcon />,
                }}
            />
            {filteredWealths?.length !== 0 && (
                <Box sx={{ px: 2, boxShadow: 2, borderRadius: 4, background: 'white' }}>
                    {filteredWealths?.map((wealth, index) => (
                        <Box key={wealth.wealthId} sx={{}}>
                            <Box onClick={() => onWealthClick(wealth)} sx={{ width: '100%', py: 0.5 }}>
                                <Stack direction="row" spacing={1} alignContent={'center'}>
                                    <Hardware sx={{ color: '#FFC600' }} />
                                    <Typography sx={{ fontSize: 16, fontWeight: 'bold', lineHeight: 1 }}>{wealth.code} - {wealth.name}</Typography>
                                </Stack>
                                {wealth.employeeCodeAndName ?
                                    <Stack direction="row" spacing={1} alignItems={'center'} sx={{ display: selectedEmployee ? 'none' : 'flex' }}>
                                        <PersonIcon sx={{ color: '#FFC600' }} />
                                        <Typography
                                            sx={{
                                                fontSize: 15,
                                                lineHeight: 1,
                                                ...(wealth.borrowedByCurrentEmployee && {
                                                    color: 'green',
                                                }),
                                            }}
                                        >
                                            {wealth.employeeCodeAndName}
                                        </Typography>
                                    </Stack>
                                    :
                                    <Stack direction="row" spacing={1} alignItems={'center'}>
                                        <CheckIcon sx={{ color: 'green' }} />
                                        <Typography sx={{ fontSize: 15, color: 'green', lineHeight: 1 }}>{t('Available')}</Typography>
                                    </Stack>
                                }
                                {wealth.buildingCodeAndName && (
                                    <Stack direction="row" spacing={1} alignItems={'center'}>
                                        <DomainOutlinedIcon sx={{ color: '#FFC600' }} />
                                        <Typography sx={{ fontSize: 15, lineHeight: 1 }}>{wealth.buildingCodeAndName}</Typography>
                                    </Stack>
                                )}
                                {(wealth.rentDateFrom || wealth.rentDateTo) && wealth.employeeCodeAndName &&
                                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                                        <DateRangeIcon sx={{ color: '#FFC600' }} />
                                        <Stack direction="row" spacing={1} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                            <Typography sx={{ fontSize: 15, lineHeight: 1 }}>
                                                {wealth.rentDateFrom && `${t('from')} ${wealth.rentDateFrom.toLocaleDateString(i18n.language)} `}
                                            </Typography>
                                            <Typography sx={{ fontSize: 15, lineHeight: 1 }}>
                                                {wealth.rentDateTo && (
                                                    <span style={{ color: new Date(wealth.rentDateTo) < new Date() ? 'red' : 'inherit' }}>
                                                        {`${t('to').toLowerCase()} ${wealth.rentDateTo.toLocaleDateString(i18n.language)}`}
                                                    </span>
                                                )}
                                            </Typography>
                                        </Stack>
                                    </Box>
                                }
                            </Box>
                            {index < filteredWealths.length - 1 && (
                                <Box sx={{ borderBottom: '1px solid #ccc' }} />
                            )}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
}
